import { useState } from 'react'
import { useUIStore } from '@/stores/ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Settings, Users, Sparkles, Zap, AlertCircle } from 'lucide-react'
import { getEnhancementCacheStats } from '@/lib/enhancement-cache'

interface PreferencesDialogProps {
  children: React.ReactNode
}

export function PreferencesDialog({ children }: PreferencesDialogProps) {
  const { preferences, setPreference } = useUIStore()
  const [open, setOpen] = useState(false)
  const cacheStats = getEnhancementCacheStats()

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Benutzereinstellungen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Interface Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Interface</CardTitle>
              <CardDescription>
                Anpassung der Benutzeroberfläche für eine optimale Arbeitsweise
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Bulk-Operationen</Label>
                  <p className="text-sm text-muted-foreground">
                    Mehrfachauswahl und Bulk-Aktionen für FAQs aktivieren
                  </p>
                </div>
                <Switch
                  checked={preferences.enableBulkOperations}
                  onCheckedChange={(checked) => setPreference('enableBulkOperations', checked)}
                />
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="space-y-0.5">
                  <Label className="text-base flex items-center gap-2">
                    <Zap className="h-4 w-4 text-yellow-500" />
                    Turbo Mode (QA Review)
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Pre-Caching von AI-Enhancements für schnelleres Review
                  </p>
                </div>
                <Switch
                  checked={preferences.enableTurboMode}
                  onCheckedChange={(checked) => setPreference('enableTurboMode', checked)}
                />
              </div>

              {preferences.enableTurboMode && (
                <div className="text-xs text-muted-foreground bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 text-yellow-600 dark:text-yellow-500" />
                    <div>
                      <p className="font-medium text-yellow-800 dark:text-yellow-200">Turbo Mode aktiv</p>
                      <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                        Enhancements werden automatisch für den nächsten Eintrag geladen.
                        Cache: {cacheStats.size}/{cacheStats.maxSize} Einträge
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 mt-0.5 text-blue-500" />
                  <div>
                    <p className="font-medium">Automatische Animationssteuerung</p>
                    <p>Animationen sind automatisch aktiviert wenn Bulk-Operationen deaktiviert sind. Dies verhindert versehentliche Klicks während Resize-Animationen.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sync Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Synchronisation</CardTitle>
              <CardDescription>
                Einstellungen werden automatisch zwischen Geräten synchronisiert
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>Gespeichert in Supabase User Metadata</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Ihre Einstellungen werden automatisch mit Ihrem Account synchronisiert und sind auf allen Geräten verfügbar.
                LocalStorage dient als Fallback wenn Sie nicht angemeldet sind.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => setOpen(false)}>
            Schließen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}